import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TablePaginationConfig } from 'antd/es/table/interface';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 30, 50] as const;

type DataViewPageSize = (typeof PAGE_SIZE_OPTIONS)[number];

interface UseDataViewPaginationOptions {
  pageParam?: string;
  pageSizeParam?: string;
}

interface UseDataViewPaginationResult<TItem> {
  currentPage: number;
  pageSize: DataViewPageSize;
  paginatedItems: TItem[];
  pagination: TablePaginationConfig;
  resetPage: () => void;
}

function parsePositiveInteger(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed;
}

function parsePageSize(value: string | null): DataViewPageSize {
  const parsed = parsePositiveInteger(value);
  if (parsed === null) return DEFAULT_PAGE_SIZE;
  return PAGE_SIZE_OPTIONS.includes(parsed as DataViewPageSize)
    ? (parsed as DataViewPageSize)
    : DEFAULT_PAGE_SIZE;
}

function normalizePage(
  requestedPage: number,
  totalItems: number,
  pageSize: DataViewPageSize,
): number {
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  return Math.min(requestedPage, pageCount);
}

export function useDataViewPagination<TItem>(
  items: readonly TItem[],
  options: UseDataViewPaginationOptions = {},
): UseDataViewPaginationResult<TItem> {
  const { pageParam = 'page', pageSizeParam = 'pageSize' } = options;
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPage = parsePositiveInteger(searchParams.get(pageParam)) ?? 1;
  const pageSize = parsePageSize(searchParams.get(pageSizeParam));
  const currentPage = normalizePage(requestedPage, items.length, pageSize);

  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [currentPage, items, pageSize]);

  function writePagination(nextPage: number, nextPageSize: number): void {
    const safePageSize = parsePageSize(String(nextPageSize));
    const safePage = normalizePage(Math.max(1, nextPage), items.length, safePageSize);
    const nextParams = new URLSearchParams(searchParams);

    if (safePage === 1) {
      nextParams.delete(pageParam);
    } else {
      nextParams.set(pageParam, String(safePage));
    }

    if (safePageSize === DEFAULT_PAGE_SIZE) {
      nextParams.delete(pageSizeParam);
    } else {
      nextParams.set(pageSizeParam, String(safePageSize));
    }

    setSearchParams(nextParams);
  }

  function resetPage(): void {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(pageParam);
    setSearchParams(nextParams);
  }

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: currentPage,
      pageSize,
      pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
      showSizeChanger: true,
      // Spec D: компактная пагинация под 42px-строками таблицы.
      size: 'small',
      total: items.length,
      onChange: writePagination,
      onShowSizeChange: writePagination,
    }),
    [currentPage, items.length, pageSize, searchParams],
  );

  return {
    currentPage,
    pageSize,
    paginatedItems,
    pagination,
    resetPage,
  };
}
